import { useCallback, type MouseEvent } from 'react';
import classnames from 'clsx';

import style from './collapsible-list-section-header.module.css';
import { CollapsibleListSectionHeaderContent } from './content';
import { noop } from '../../../default-props/noop';
import { generateDataTestidAttribute } from '../../../services/testid-service';

import type { ClickHandler, CollapsibleListSectionHeaderType } from './collapsible-list-section-header-types';

export const CollapsibleListSectionHeader: CollapsibleListSectionHeaderType = ({
  children,
  theme = '',
  disabled = false,
  open = false,
  onClick: onClickProp = noop,
  alwaysClosed = false,
  dragHandleProps,
  className,
  openExpanderClassName,
  summaryData,
  summaryDataStyle,
  noIcon = false,
  name,
  amplitudeTrackingId,
  shouldPositionCaretRight = false,
  openCloseCaretVertical = false,
  contentClassName,
  caretClassName,
  grabberIconClassName,
}) => {
  const active = !alwaysClosed && !noIcon;
  const classNameList = classnames(style[theme], style['collapsible-list-item-header'], {
    [style.disabled]: disabled,
    [style.open]: open,
    [style['no-outline']]: typeof children === 'function',
    [style.inactive]: !active,
  });

  const onClick: ClickHandler = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (alwaysClosed) {
        return;
      }
      onClickProp(event);
    },
    [alwaysClosed, onClickProp],
  );

  if (typeof children === 'function') {
    return (
      <>
        <div
          className={classNameList}
          data-amplitude-id={amplitudeTrackingId}
          {...generateDataTestidAttribute('collapsible-list-section-header')}
          data-name={name}
          onClick={onClick}
          role='button' // oxlint-disable-line jsx-a11y/prefer-tag-over-role
          tabIndex={0}
        >
          <CollapsibleListSectionHeaderContent
            active={active}
            caretClassName={caretClassName}
            className={className}
            contentClassName={contentClassName}
            dragHandleProps={dragHandleProps}
            grabberIconClassName={grabberIconClassName}
            open={open}
            openCloseCaretVertical={openCloseCaretVertical}
            openExpanderClassName={openExpanderClassName}
            shouldPositionCaretRight={shouldPositionCaretRight}
            summaryData={summaryData}
            summaryDataStyle={summaryDataStyle}
          >
            {children()}
          </CollapsibleListSectionHeaderContent>
        </div>
      </>
    );
  }

  return (
    <div
      className={classNameList}
      data-amplitude-id={amplitudeTrackingId}
      {...generateDataTestidAttribute('collapsible-list-section-header')}
      data-name={name}
      onClick={onClick}
      role='button' // oxlint-disable-line jsx-a11y/prefer-tag-over-role
      tabIndex={-1}
    >
      <CollapsibleListSectionHeaderContent
        active={active}
        caretClassName={caretClassName}
        className={className}
        contentClassName={contentClassName}
        dragHandleProps={dragHandleProps}
        grabberIconClassName={grabberIconClassName}
        open={open}
        openCloseCaretVertical={openCloseCaretVertical}
        openExpanderClassName={openExpanderClassName}
        shouldPositionCaretRight={shouldPositionCaretRight}
        summaryData={summaryData}
        summaryDataStyle={summaryDataStyle}
      >
        {children}
      </CollapsibleListSectionHeaderContent>
    </div>
  );
};
